/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Search, Filter, ChevronDown, Check, Download, FileImage, FileText, Table as TableIcon,
  TrendingUp, DollarSign, Target, Send, CheckCircle2, Eye, MousePointer, Users, Tag,
  Link as LinkIcon, ExternalLink, ArrowUp, ArrowDown, Award, Sparkles, AlertTriangle,
  Zap, LayoutGrid, BarChart3, Bookmark, BookmarkPlus, Star, MoreVertical, Trash2, Edit3, Save, X,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, ComposedChart,
} from 'recharts';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';
import CreativeImage from '../../components/CreativeImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#32B4AC';

// ==================== Formatting helpers ====================
const fmtInr = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};
const fmtNum = (v) => Number(v || 0).toLocaleString('en-IN');
const fmtPct = (v) => `${Number(v || 0).toFixed(1)}%`;
const fmtRoas = (v) => (v != null && v !== '' ? `${Number(v).toFixed(2)}x` : '—');
const trunc = (s, max = 32) => (s && s.length > max ? `${s.slice(0, max - 1)}…` : s || '');

// ==================== Date parsing / formatting ====================
// Robust parser: handles ISO (YYYY-MM-DD), DD-MM-YYYY / DD/MM/YYYY (Indian convention),
// MM-DD-YYYY / MM/DD/YYYY (US), and any string Date.parse understands. Returns
// a JS Date or null. NEVER assumes character position — every branch is validated.
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseAnyDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  const s = String(raw).trim();
  if (!s) return null;

  // ISO YYYY-MM-DD or YYYY/MM/DD
  let m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) {
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // DD-MM-YYYY or DD/MM/YYYY (Indian/EU convention) — try first because our users are IN
  m = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) {
    const dd = +m[1], mm = +m[2], yy = +m[3];
    // If day > 12, must be DD-MM. If month > 12, it's actually MM-DD (swap).
    let day = dd, month = mm;
    if (dd <= 12 && mm > 12) { day = mm; month = dd; }
    const d = new Date(Date.UTC(yy, month - 1, day));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Fallback: let JS try
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Bucket key: `YYYY-MM` derived from a parsed Date (not from string ops). */
const bucketKey = (date) => {
  if (!date) return null;
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** Convert `YYYY-MM` → `MMM YYYY` (e.g. `Apr 2025`). */
const fmtMonthLabel = (key) => {
  if (!key || !/^\d{4}-\d{2}$/.test(key)) return key || '';
  const [y, m] = key.split('-');
  return `${MONTHS_SHORT[+m - 1]} ${y}`;
};

/** Convert Date or ISO/DD-MM string → `DD MMM YYYY` (e.g. `05 Dec 2026`). */
const fmtFullDate = (raw) => {
  const d = parseAnyDate(raw);
  if (!d) return String(raw || '—');
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// ==================== Multi-select dropdown ====================
const MultiSelect = ({ label, options, values, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const filtered = options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()));
  const summary = values.length === 0 ? `All ${label.toLowerCase()}` :
    values.length === 1 ? (options.find((o) => o.value === values[0])?.label || values[0]) :
      `${values.length} selected`;
  return (
    <div ref={boxRef} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-left text-xs flex items-center justify-between hover:border-teal-400 transition-colors"
      >
        <span className="text-slate-700 truncate">{summary}</span>
        <ChevronDown size={14} className={`text-slate-400 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-xl right-0 max-h-80 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}…`}
                className="w-full pl-7 pr-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded"
              />
            </div>
            <div className="flex justify-between mt-1.5">
              <button onClick={() => onChange(filtered.map((o) => o.value))} className="text-[10px] text-teal-600 font-semibold hover:underline">Select all</button>
              <button onClick={() => onChange([])} className="text-[10px] text-slate-500 font-semibold hover:underline">Clear</button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="p-3 text-xs text-slate-400 text-center">No matches</div>
            ) : filtered.map((o) => {
              const checked = values.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => onChange(checked ? values.filter((v) => v !== o.value) : [...values, o.value])}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 text-left"
                >
                  <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border ${checked ? 'bg-teal-500 border-teal-500' : 'border-slate-300 bg-white'}`}>
                    {checked && <Check size={9} className="text-white" />}
                  </span>
                  {o.color && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: o.color }} />}
                  <span className="text-slate-700 flex-1 truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Performance tag ====================
const PERF_TAGS = [
  { min: 5.0, label: 'Top Performer',    accent: 'from-emerald-500 to-teal-500' },
  { min: 3.0, label: 'Great Choice',     accent: 'from-blue-500 to-indigo-500' },
  { min: 1.5, label: 'Good Option',      accent: 'from-amber-500 to-orange-500' },
  { min: -Infinity, label: 'Needs Improvement', accent: 'from-rose-500 to-red-500' },
];
const pickTag = (roas) => PERF_TAGS.find((t) => (Number(roas) || 0) >= t.min);

// ==================== Table columns registry ====================
// `always: true` means the column can never be hidden (essential for row identity).
const TABLE_COLS = [
  { key: 'creative',     label: 'Creative',     always: true },
  { key: 'name',         label: 'Campaign',     always: true, sortable: true },
  { key: 'brand_id',     label: 'Brand',        sortable: true },
  { key: 'date',         label: 'Date',         sortable: true, align: 'right' },
  { key: 'sales',        label: 'Revenue',      sortable: true, align: 'right' },
  { key: 'net_spends',   label: 'Spend',        sortable: true, align: 'right' },
  { key: 'roas',         label: 'ROAS',         sortable: true, align: 'right' },
  { key: 'ctr',          label: 'CTR',          sortable: true, align: 'right' },
  { key: 'delivery_rate', label: 'Delivery',    sortable: true, align: 'right' },
  { key: 'sent',         label: 'Sent',         sortable: true, align: 'right' },
  { key: 'delivered',    label: 'Delivered',    sortable: true, align: 'right' },
  { key: 'seen',         label: 'Seen',         sortable: true, align: 'right' },
  { key: 'clicks',       label: 'Clicks',       sortable: true, align: 'right' },
  { key: 'cohort',       label: 'Cohort' },
  { key: 'attribution_setting', label: 'Attribution' },
  { key: 'landing_page', label: 'Landing' },
];
const DEFAULT_VISIBLE_COLS = TABLE_COLS.map((c) => c.key);

// ==================== KPI Card ====================
const KpiCard = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-2 mb-1.5">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${accent || 'bg-slate-100 text-slate-600'}`}>
        <Icon size={14} />
      </div>
      <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xl font-bold text-slate-900">{value}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

// ==================== Main Page ====================
const OneViewAnalyzer = ({ showToast }) => {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';

  // ---------- Data ----------
  const [campaigns, setCampaigns] = useState([]);
  const [automation, setAutomation] = useState([]);
  const [brands, setBrands] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const boardRef = useRef(null);

  // ---------- Filters ----------
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [brandIds, setBrandIds] = useState([]);
  const [campaignQ, setCampaignQ] = useState('');
  const [typeIds, setTypeIds] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [attributions, setAttributions] = useState([]);

  // ---------- Table state ----------
  const [tableSearch, setTableSearch] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedRows, setSelectedRows] = useState([]);
  const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE_COLS);

  // ---------- Saved Views state ----------
  const [savedViews, setSavedViews] = useState([]);
  const [currentViewId, setCurrentViewId] = useState(null);
  const defaultLoadedRef = useRef(false);

  // ---------- Compare state ----------
  const [compareIds, setCompareIds] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, a, b, t, sv] = await Promise.all([
          axios.get(`${API}/campaigns-data`),
          axios.get(`${API}/automation-campaigns`),
          axios.get(`${API}/brands`),
          axios.get(`${API}/campaign-types`),
          axios.get(`${API}/saved-views?scope=one_view`).catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        setCampaigns(c.data || []);
        setAutomation(a.data || []);
        setBrands(b.data || []);
        setTypes(t.data || []);
        setSavedViews(sv.data || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('OneView load failed', err);
        showToast?.('Failed to load analytics data', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [showToast]);

  // Apply default view once on first load (after data is ready).
  // This runs exactly once — user is free to switch to any view afterwards.
  useEffect(() => {
    if (defaultLoadedRef.current) return;
    if (loading) return;
    const def = savedViews.find((v) => v.is_default);
    if (def) {
      applyView(def, { silent: true });
    }
    defaultLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, savedViews]);

  // ---------- Saved Views: config snapshot / apply ----------
  const currentConfig = () => ({
    dateFrom, dateTo, brandIds, campaignQ, typeIds, cohorts, attributions,
    tableSearch, sortKey, sortDir, visibleCols,
  });

  const applyView = (view, opts = {}) => {
    const cfg = view?.config || {};
    setDateFrom(cfg.dateFrom || '');
    setDateTo(cfg.dateTo || '');
    setBrandIds(Array.isArray(cfg.brandIds) ? cfg.brandIds : []);
    setCampaignQ(cfg.campaignQ || '');
    setTypeIds(Array.isArray(cfg.typeIds) ? cfg.typeIds : []);
    setCohorts(Array.isArray(cfg.cohorts) ? cfg.cohorts : []);
    setAttributions(Array.isArray(cfg.attributions) ? cfg.attributions : []);
    setTableSearch(cfg.tableSearch || '');
    setSortKey(cfg.sortKey || 'date');
    setSortDir(cfg.sortDir || 'desc');
    setVisibleCols(Array.isArray(cfg.visibleCols) && cfg.visibleCols.length ? cfg.visibleCols : DEFAULT_VISIBLE_COLS);
    setCurrentViewId(view.id);
    if (!opts.silent) showToast?.(`Loaded view: ${view.name}`, 'success');
  };

  const refreshSavedViews = async () => {
    try {
      const res = await axios.get(`${API}/saved-views?scope=one_view`);
      setSavedViews(res.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Refresh saved views failed', e);
    }
  };

  const createSavedView = async (name, setDefault = false) => {
    try {
      const res = await axios.post(`${API}/saved-views`, {
        name, scope: 'one_view', config: currentConfig(), is_default: setDefault,
      });
      await refreshSavedViews();
      setCurrentViewId(res.data.id);
      showToast?.(`Saved view: ${res.data.name}`, 'success');
      return res.data;
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save view';
      showToast?.(msg, 'error');
      return null;
    }
  };

  const updateSavedView = async (viewId, payload) => {
    try {
      await axios.put(`${API}/saved-views/${viewId}`, payload);
      await refreshSavedViews();
      showToast?.('View updated', 'success');
    } catch (e) {
      showToast?.(e?.response?.data?.detail || 'Failed to update view', 'error');
    }
  };

  const overwriteCurrentView = async () => {
    if (!currentViewId) return;
    await updateSavedView(currentViewId, { config: currentConfig() });
  };

  const renameSavedView = async (viewId, newName) => {
    await updateSavedView(viewId, { name: newName });
  };

  const setDefaultSavedView = async (viewId) => {
    try {
      await axios.post(`${API}/saved-views/${viewId}/set-default`);
      await refreshSavedViews();
      showToast?.('Set as default view', 'success');
    } catch (e) {
      showToast?.(e?.response?.data?.detail || 'Failed to set default', 'error');
    }
  };

  const deleteSavedView = async (viewId) => {
    try {
      await axios.delete(`${API}/saved-views/${viewId}`);
      if (currentViewId === viewId) setCurrentViewId(null);
      await refreshSavedViews();
      showToast?.('View deleted', 'success');
    } catch (e) {
      showToast?.(e?.response?.data?.detail || 'Failed to delete view', 'error');
    }
  };

  const brandMap = useMemo(() => {
    const m = {};
    brands.forEach((b) => { m[b.id] = b; });
    return m;
  }, [brands]);

  // Derive dropdown options from actual data (Cohort, Attribution)
  const cohortOptions = useMemo(() => {
    const set = new Set();
    campaigns.forEach((c) => { if (c.cohort) set.add(c.cohort); });
    return Array.from(set).sort().map((v) => ({ value: v, label: v }));
  }, [campaigns]);
  const attributionOptions = useMemo(() => {
    const set = new Set();
    campaigns.forEach((c) => { if (c.attribution_setting) set.add(c.attribution_setting); });
    return Array.from(set).sort().map((v) => ({ value: v, label: v }));
  }, [campaigns]);

  // ---------- Filtered campaigns ----------
  const filtered = useMemo(() => {
    return campaigns.filter((c) => {
      if (dateFrom && c.date && c.date < dateFrom) return false;
      if (dateTo && c.date && c.date > dateTo) return false;
      if (brandIds.length && !brandIds.includes(c.brand_id)) return false;
      if (campaignQ && !(c.name || '').toLowerCase().includes(campaignQ.toLowerCase())) return false;
      if (typeIds.length && !typeIds.includes(c.type_id)) return false;
      if (cohorts.length && !cohorts.includes(c.cohort)) return false;
      if (attributions.length && !attributions.includes(c.attribution_setting)) return false;
      return true;
    });
  }, [campaigns, dateFrom, dateTo, brandIds, campaignQ, typeIds, cohorts, attributions]);

  // ---------- KPI aggregates ----------
  const kpis = useMemo(() => {
    const sum = (k) => filtered.reduce((acc, c) => acc + (Number(c[k]) || 0), 0);
    const totalSent = sum('sent');
    const totalDelivered = sum('delivered');
    const totalSeen = sum('seen');
    const totalClicks = sum('clicks');
    const totalRevenue = sum('sales');
    const totalSpend = sum('net_spends');
    return {
      count: filtered.length,
      revenue: totalRevenue,
      spend: totalSpend,
      roas: totalSpend > 0 ? totalRevenue / totalSpend : 0,
      sent: totalSent,
      delivered: totalDelivered,
      seen: totalSeen,
      clicks: totalClicks,
      ctr: totalDelivered > 0 ? (totalClicks / totalDelivered) * 100 : 0,
      deliveryRate: totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0,
    };
  }, [filtered]);

  // ---------- Trend series (monthly, chronologically ordered) ----------
  const trendSeries = useMemo(() => {
    const buckets = {};
    filtered.forEach((c) => {
      const parsed = parseAnyDate(c.date);
      const key = bucketKey(parsed);
      if (!key) return; // silently skip rows with unparseable dates
      const b = buckets[key] || {
        key,
        // store the earliest timestamp seen for this bucket, used only for sort
        _ts: parsed.getTime(),
        revenue: 0, spend: 0, ctrN: 0, ctrD: 0, drN: 0, drD: 0,
      };
      b.revenue += Number(c.sales) || 0;
      b.spend += Number(c.net_spends) || 0;
      b.ctrN += Number(c.clicks) || 0;
      b.ctrD += Number(c.delivered) || 0;
      b.drN += Number(c.delivered) || 0;
      b.drD += Number(c.sent) || 0;
      buckets[key] = b;
    });
    return Object.values(buckets)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((b) => ({
        key: b.key,
        // Display label = bucket key itself (YYYY-MM), never a day component.
        label: b.key,
        revenue: b.revenue,
        spend: b.spend,
        roas: b.spend > 0 ? b.revenue / b.spend : 0,
        ctr: b.ctrD > 0 ? (b.ctrN / b.ctrD) * 100 : 0,
        delivery_rate: b.drD > 0 ? (b.drN / b.drD) * 100 : 0,
      }));
  }, [filtered]);

  // Auto-thin XAxis labels for long ranges to prevent overlap while keeping
  // chronological order intact. 0 = show every label; larger = skip every N.
  const trendTickInterval = useMemo(() => {
    const n = trendSeries.length;
    if (n <= 8) return 0;                  // ≤8 months: show all
    if (n <= 16) return 1;                 // 16 months: skip every other
    return Math.ceil(n / 10) - 1;          // aim for ~10 visible ticks
  }, [trendSeries.length]);

  // ---------- Per-brand aggregation ----------
  const brandAgg = useMemo(() => {
    const map = {};
    filtered.forEach((c) => {
      const bid = c.brand_id || 'unknown';
      const b = map[bid] || { brand_id: bid, name: brandMap[bid]?.name || 'Unknown', color: brandMap[bid]?.color || '#94a3b8', count: 0, revenue: 0, spend: 0, sent: 0, delivered: 0, clicks: 0 };
      b.count += 1;
      b.revenue += Number(c.sales) || 0;
      b.spend += Number(c.net_spends) || 0;
      b.sent += Number(c.sent) || 0;
      b.delivered += Number(c.delivered) || 0;
      b.clicks += Number(c.clicks) || 0;
      map[bid] = b;
    });
    return Object.values(map).map((b) => ({
      ...b,
      roas: b.spend > 0 ? b.revenue / b.spend : 0,
      ctr: b.delivered > 0 ? (b.clicks / b.delivered) * 100 : 0,
    })).sort((a, b) => b.revenue - a.revenue);
  }, [filtered, brandMap]);

  // ---------- Automation Analytics slice (respects brand filter only, since automation uses free-text brand strings) ----------
  const automationAgg = useMemo(() => {
    const brandNameSet = brandIds.length > 0
      ? new Set(brandIds.map((id) => (brandMap[id]?.name || '').toLowerCase()))
      : null;
    const filteredAuto = automation.filter((r) => {
      if (!brandNameSet) return true;
      // Fuzzy match by prefix on brand text (e.g. 'Wellcore_PDP_Drop' → 'wellcore')
      const brandStr = (r.brand || '').toLowerCase();
      for (const bname of brandNameSet) {
        if (bname && brandStr.startsWith(bname)) return true;
      }
      return false;
    });

    const totalRev = filteredAuto.reduce((a, r) => a + (Number(r.sales) || 0), 0);
    const totalSpend = filteredAuto.reduce((a, r) => a + (Number(r.spend) || 0), 0);
    const perBrand = {};
    filteredAuto.forEach((r) => {
      const key = r.brand || 'unknown';
      const b = perBrand[key] || { brand: key, revenue: 0, spend: 0, sent: 0, clicks: 0 };
      b.revenue += Number(r.sales) || 0;
      b.spend += Number(r.spend) || 0;
      b.sent += Number(r.sent) || 0;
      b.clicks += Number(r.clicks) || 0;
      perBrand[key] = b;
    });
    const topAutos = Object.values(perBrand).sort((a, b) => b.revenue - a.revenue).slice(0, 5);
    return {
      totalRevenue: totalRev,
      totalSpend,
      roas: totalSpend > 0 ? totalRev / totalSpend : 0,
      top: topAutos,
    };
  }, [automation, brandIds, brandMap]);

  // ---------- Automation vs Manual (revenue) ----------
  const autoVsManual = useMemo(() => {
    return [
      { source: 'Manual', revenue: kpis.revenue, spend: kpis.spend },
      { source: 'Automation', revenue: automationAgg.totalRevenue, spend: automationAgg.totalSpend },
    ];
  }, [kpis, automationAgg]);

  // ---------- Sorted + searched table ----------
  const tableRows = useMemo(() => {
    const q = tableSearch.toLowerCase();
    const rows = filtered.filter((c) => {
      if (!q) return true;
      const b = brandMap[c.brand_id]?.name || '';
      return (c.name || '').toLowerCase().includes(q) || b.toLowerCase().includes(q)
        || (c.cohort || '').toLowerCase().includes(q)
        || (c.attribution_setting || '').toLowerCase().includes(q);
    });
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      const na = Number(av);
      const nb = Number(bv);
      const isNumeric = !Number.isNaN(na) && !Number.isNaN(nb);
      let cmp;
      if (isNumeric) cmp = na - nb;
      else cmp = String(av || '').localeCompare(String(bv || ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [filtered, tableSearch, sortKey, sortDir, brandMap]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleRow = (id) => {
    setSelectedRows((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // ---------- Leaderboards ----------
  const leaderboards = useMemo(() => ({
    topRevenue: [...filtered].sort((a, b) => (Number(b.sales) || 0) - (Number(a.sales) || 0)).slice(0, 5),
    topRoas:    [...filtered].sort((a, b) => (Number(b.roas)  || 0) - (Number(a.roas)  || 0)).slice(0, 5),
    topCtr:     [...filtered].sort((a, b) => (Number(b.ctr)   || 0) - (Number(a.ctr)   || 0)).slice(0, 5),
    topBrands:  brandAgg.slice(0, 5),
    topAutomation: automationAgg.top,
  }), [filtered, brandAgg, automationAgg]);

  // ---------- AI Insights ----------
  const insights = useMemo(() => {
    if (filtered.length === 0) return null;
    const best = leaderboards.topRoas[0];
    const worst = [...filtered].filter((c) => (Number(c.roas) || 0) > 0).sort((a, b) => (Number(a.roas) || 0) - (Number(b.roas) || 0))[0];
    const bestBrand = brandAgg[0];
    // Best cohort by revenue
    const cohortMap = {};
    filtered.forEach((c) => {
      if (!c.cohort) return;
      cohortMap[c.cohort] = (cohortMap[c.cohort] || { name: c.cohort, revenue: 0, count: 0, ctr: 0, ctrN: 0, ctrD: 0 });
      cohortMap[c.cohort].revenue += Number(c.sales) || 0;
      cohortMap[c.cohort].count += 1;
      cohortMap[c.cohort].ctrN += Number(c.clicks) || 0;
      cohortMap[c.cohort].ctrD += Number(c.delivered) || 0;
    });
    const bestCohort = Object.values(cohortMap).map((c) => ({ ...c, ctr: c.ctrD > 0 ? (c.ctrN / c.ctrD) * 100 : 0 })).sort((a, b) => b.revenue - a.revenue)[0];
    // Best landing page by revenue
    const lpMap = {};
    filtered.forEach((c) => {
      if (!c.landing_page) return;
      lpMap[c.landing_page] = (lpMap[c.landing_page] || { url: c.landing_page, revenue: 0, count: 0 });
      lpMap[c.landing_page].revenue += Number(c.sales) || 0;
      lpMap[c.landing_page].count += 1;
    });
    const bestLp = Object.values(lpMap).sort((a, b) => b.revenue - a.revenue)[0];

    // Suggestions
    const suggestions = [];
    if (worst && best && Number(worst.roas) < 1.5 && Number(best.roas) >= 3) {
      suggestions.push(`Reallocate budget from "${trunc(worst.name, 32)}" (ROAS ${fmtRoas(worst.roas)}) to "${trunc(best.name, 32)}" (ROAS ${fmtRoas(best.roas)}).`);
    }
    if (kpis.deliveryRate < 90) {
      suggestions.push(`Delivery rate is ${fmtPct(kpis.deliveryRate)} — check opt-in health and template quality.`);
    }
    if (kpis.ctr < 3 && kpis.delivered > 0) {
      suggestions.push(`Overall CTR is ${fmtPct(kpis.ctr)} — consider A/B testing creatives on low-performing campaigns.`);
    }
    if (bestCohort && bestCohort.count >= 2) {
      suggestions.push(`Cohort "${bestCohort.name}" drives ${fmtInr(bestCohort.revenue)} across ${bestCohort.count} campaigns — expand targeting.`);
    }
    if (suggestions.length === 0) {
      suggestions.push('All key metrics look healthy. Keep monitoring for outliers.');
    }
    return { best, worst, bestBrand, bestCohort, bestLp, suggestions };
  }, [filtered, leaderboards, brandAgg, kpis]);

  // ---------- Compare ----------
  const openCompare = () => {
    if (selectedRows.length < 2) {
      showToast?.('Select at least 2 campaigns to compare', 'error');
      return;
    }
    if (selectedRows.length > 5) {
      showToast?.('Maximum 5 campaigns can be compared', 'error');
      return;
    }
    setCompareIds(selectedRows);
    setCompareOpen(true);
  };

  const compareItems = useMemo(
    () => compareIds.map((id) => campaigns.find((c) => c.id === id)).filter(Boolean),
    [compareIds, campaigns],
  );

  // Best-per-metric across compareItems (for highlighting)
  const compareBest = useMemo(() => {
    if (compareItems.length < 2) return {};
    const keys = ['sales', 'net_spends', 'ctr', 'delivery_rate', 'roas'];
    const out = {};
    keys.forEach((k) => {
      const vals = compareItems.map((c) => Number(c[k] || 0));
      out[k] = Math.max(...vals);
    });
    return out;
  }, [compareItems]);

  // ---------- Exports ----------
  const exportTableCsv = () => {
    const rows = tableRows;
    const header = ['Campaign', 'Brand', 'Date', 'Revenue', 'Spend', 'ROAS', 'CTR', 'Delivery Rate', 'Sent', 'Delivered', 'Seen', 'Clicks', 'Cohort', 'Attribution', 'Landing Page'];
    const body = rows.map((c) => [
      c.name || '',
      brandMap[c.brand_id]?.name || '',
      c.date || '',
      c.sales || 0, c.net_spends || 0, c.roas || 0, c.ctr || 0, c.delivery_rate || 0,
      c.sent || 0, c.delivered || 0, c.seen || 0, c.clicks || 0,
      c.cohort || '', c.attribution_setting || '', c.landing_page || '',
    ]);
    const csv = [header, ...body].map((row) =>
      row.map((v) => {
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `one-view-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast?.('Exported CSV', 'success');
  };

  const exportTableXlsx = () => {
    const rows = tableRows.map((c) => ({
      Campaign: c.name || '',
      Brand: brandMap[c.brand_id]?.name || '',
      Date: c.date || '',
      Revenue: Number(c.sales) || 0,
      Spend: Number(c.net_spends) || 0,
      ROAS: Number(c.roas) || 0,
      CTR: Number(c.ctr) || 0,
      'Delivery Rate': Number(c.delivery_rate) || 0,
      Sent: Number(c.sent) || 0,
      Delivered: Number(c.delivered) || 0,
      Seen: Number(c.seen) || 0,
      Clicks: Number(c.clicks) || 0,
      Cohort: c.cohort || '',
      Attribution: c.attribution_setting || '',
      'Landing Page': c.landing_page || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'One View');
    XLSX.writeFile(wb, `one-view-${new Date().toISOString().slice(0, 10)}.xlsx`);
    showToast?.('Exported Excel', 'success');
  };

  const exportBoard = async (format) => {
    if (!boardRef.current) return;
    try {
      setExporting(true);
      const dataUrl = await toPng(boardRef.current, {
        backgroundColor: '#f8fafc', pixelRatio: 2, cacheBust: true, skipFonts: true,
      });
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      if (format === 'pdf') {
        const img = new Image(); img.src = dataUrl;
        await new Promise((res) => { img.onload = res; if (img.complete) res(); });
        const orientation = img.width >= img.height ? 'landscape' : 'portrait';
        const pdf = new jsPDF({ orientation, unit: 'pt', format: [img.width, img.height], compress: true });
        pdf.addImage(dataUrl, 'PNG', 0, 0, img.width, img.height);
        pdf.save(`one-view-${ts}.pdf`);
      } else {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `one-view-${ts}.png`;
        a.click();
      }
      showToast?.(`Exported ${format.toUpperCase()}`, 'success');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      showToast?.('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setDateFrom(''); setDateTo(''); setBrandIds([]); setCampaignQ('');
    setTypeIds([]); setCohorts([]); setAttributions([]);
  };

  // ---------- Render ----------
  if (loading) {
    return <div className="p-8 text-slate-500 text-sm" data-testid="one-view-loading">Loading One View Analyzer…</div>;
  }

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="one-view-analyzer">
      {/* Sticky filters + header */}
      <div className="sticky top-0 z-30 -mx-4 md:-mx-6 px-4 md:px-6 pb-3 pt-3 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: TEAL }}>
              <LayoutGrid size={18} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">One View Analyzer</h1>
              <p className="text-xs text-slate-500">Executive dashboard across all brands · {filtered.length} campaigns</p>
            </div>
          </div>
          {!isViewer && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="one-view-export-toolbar">
              <SavedViewsPicker
                views={savedViews}
                currentId={currentViewId}
                onApply={applyView}
                onCreate={createSavedView}
                onOverwrite={overwriteCurrentView}
                onRename={renameSavedView}
                onDelete={deleteSavedView}
                onSetDefault={setDefaultSavedView}
              />
              <button onClick={() => exportBoard('png')} disabled={exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-teal-400 text-xs font-medium text-slate-700 disabled:opacity-50"
                data-testid="one-view-export-png">
                <FileImage size={12} /> PNG
              </button>
              <button onClick={() => exportBoard('pdf')} disabled={exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-teal-400 text-xs font-medium text-slate-700 disabled:opacity-50"
                data-testid="one-view-export-pdf">
                <FileText size={12} /> PDF
              </button>
              <button onClick={exportTableCsv}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-teal-400 text-xs font-medium text-slate-700"
                data-testid="one-view-export-csv">
                <TableIcon size={12} /> CSV
              </button>
              <button onClick={exportTableXlsx}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-semibold"
                style={{ backgroundColor: TEAL }}
                data-testid="one-view-export-xlsx">
                <Download size={12} /> Excel
              </button>
            </div>
          )}
          {isViewer && (
            /* Viewers can still LOAD saved views, but not create/update/delete. */
            <div data-testid="one-view-viewer-toolbar">
              <SavedViewsPicker
                views={savedViews}
                currentId={currentViewId}
                onApply={applyView}
                readOnly
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="one-view-filters">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded-lg" data-testid="filter-date-from" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded-lg" data-testid="filter-date-to" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Brand</label>
            <MultiSelect
              label="Brands"
              options={brands.map((b) => ({ value: b.id, label: b.name, color: b.color }))}
              values={brandIds}
              onChange={setBrandIds}
              testid="filter-brand"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Campaign</label>
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" value={campaignQ} onChange={(e) => setCampaignQ(e.target.value)}
                placeholder="Search…" className="w-full pl-7 pr-2 py-2 text-xs bg-white border border-slate-200 rounded-lg"
                data-testid="filter-campaign-q" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Type</label>
            <MultiSelect
              label="Types"
              options={types.map((t) => ({ value: t.id, label: t.name, color: t.color }))}
              values={typeIds}
              onChange={setTypeIds}
              testid="filter-type"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Cohort</label>
            <MultiSelect label="Cohorts" options={cohortOptions} values={cohorts} onChange={setCohorts} testid="filter-cohort" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Attribution</label>
            <MultiSelect label="Attributions" options={attributionOptions} values={attributions} onChange={setAttributions} testid="filter-attribution" />
          </div>
        </div>
        {(dateFrom || dateTo || brandIds.length || campaignQ || typeIds.length || cohorts.length || attributions.length) > 0 && (
          <div className="mt-2">
            <button onClick={clearFilters} className="text-[11px] text-teal-600 font-semibold hover:underline" data-testid="clear-filters">
              Clear all filters
            </button>
          </div>
        )}
      </div>

      <div ref={boardRef} className="space-y-5">
        {/* ============ KPI CARDS ============ */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="one-view-kpis">
          <KpiCard label="Campaigns"     value={kpis.count}                              icon={LayoutGrid}   accent="bg-slate-100 text-slate-700" />
          <KpiCard label="Revenue"       value={fmtInr(kpis.revenue)}                    icon={DollarSign}   accent="bg-teal-100 text-teal-700" />
          <KpiCard label="Net Spend"     value={fmtInr(kpis.spend)}                      icon={DollarSign}   accent="bg-rose-100 text-rose-700" />
          <KpiCard label="ROAS"          value={fmtRoas(kpis.roas)}                      icon={Target}       accent="bg-orange-100 text-orange-700" />
          <KpiCard label="Sent"          value={fmtNum(kpis.sent)}                       icon={Send}         accent="bg-blue-100 text-blue-700" />
          <KpiCard label="Delivered"     value={fmtNum(kpis.delivered)}                  icon={CheckCircle2} accent="bg-green-100 text-green-700" />
          <KpiCard label="Seen"          value={fmtNum(kpis.seen)}                       icon={Eye}          accent="bg-purple-100 text-purple-700" />
          <KpiCard label="Clicks"        value={fmtNum(kpis.clicks)}                     icon={MousePointer} accent="bg-yellow-100 text-yellow-700" />
          <KpiCard label="CTR"           value={fmtPct(kpis.ctr)}                        icon={MousePointer} accent="bg-yellow-100 text-yellow-700" />
          <KpiCard label="Delivery Rate" value={fmtPct(kpis.deliveryRate)}               icon={TrendingUp}   accent="bg-emerald-100 text-emerald-700" />
        </div>

        {/* ============ CHARTS ============ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="one-view-charts">
          <ChartCard title="Revenue Trend" testid="chart-revenue">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendSeries} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={trendTickInterval} minTickGap={20} padding={{ right: 24 }} />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={fmtInr} />
                <RTooltip
                  formatter={(v) => fmtInr(v)}
                  labelFormatter={(l, payload) => (payload?.[0]?.payload?.key || l)}
                />
                <Line type="monotone" dataKey="revenue" stroke={TEAL} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="ROAS Trend" testid="chart-roas">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendSeries} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={trendTickInterval} minTickGap={20} padding={{ right: 24 }} />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => `${v.toFixed(1)}x`} />
                <RTooltip
                  formatter={(v) => `${Number(v).toFixed(2)}x`}
                  labelFormatter={(l, payload) => (payload?.[0]?.payload?.key || l)}
                />
                <Line type="monotone" dataKey="roas" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="CTR Trend" testid="chart-ctr">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendSeries} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={trendTickInterval} minTickGap={20} padding={{ right: 24 }} />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={(v) => `${v.toFixed(1)}%`} />
                <RTooltip
                  formatter={(v) => fmtPct(v)}
                  labelFormatter={(l, payload) => (payload?.[0]?.payload?.key || l)}
                />
                <Line type="monotone" dataKey="ctr" stroke="#eab308" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Delivery Rate Trend" testid="chart-delivery">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendSeries} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" interval={trendTickInterval} minTickGap={20} padding={{ right: 24 }} />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                <RTooltip
                  formatter={(v) => fmtPct(v)}
                  labelFormatter={(l, payload) => (payload?.[0]?.payload?.key || l)}
                />
                <Line type="monotone" dataKey="delivery_rate" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Brand Comparison" testid="chart-brand-comparison">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={brandAgg.slice(0, 8)} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={fmtInr} />
                <RTooltip formatter={(v) => fmtInr(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" fill={TEAL} name="Revenue" radius={[4, 4, 0, 0]} />
                <Bar dataKey="spend" fill="#f87171" name="Spend" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Automation vs Manual" testid="chart-auto-manual">
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={autoVsManual} margin={{ top: 10, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="source" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={fmtInr} />
                <RTooltip formatter={(v) => fmtInr(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" fill={TEAL} name="Revenue" radius={[4, 4, 0, 0]} />
                <Bar dataKey="spend" fill="#f87171" name="Spend" radius={[4, 4, 0, 0]} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        {/* ============ LEADERBOARDS ============ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="one-view-leaderboards">
          <LeaderCard title="Top Revenue" icon={DollarSign} accent="text-teal-600" items={leaderboards.topRevenue.map((c) => ({
            label: c.name, sub: brandMap[c.brand_id]?.name, value: fmtInr(c.sales),
          }))} />
          <LeaderCard title="Top ROAS" icon={Target} accent="text-orange-600" items={leaderboards.topRoas.map((c) => ({
            label: c.name, sub: brandMap[c.brand_id]?.name, value: fmtRoas(c.roas),
          }))} />
          <LeaderCard title="Top CTR" icon={MousePointer} accent="text-yellow-600" items={leaderboards.topCtr.map((c) => ({
            label: c.name, sub: brandMap[c.brand_id]?.name, value: fmtPct(c.ctr),
          }))} />
          <LeaderCard title="Top Brands" icon={Award} accent="text-emerald-600" items={leaderboards.topBrands.map((b) => ({
            label: b.name, sub: `${b.count} campaigns`, value: fmtInr(b.revenue), color: b.color,
          }))} />
          <LeaderCard title="Top Automation" icon={Zap} accent="text-blue-600" items={leaderboards.topAutomation.map((a) => ({
            label: a.brand, sub: '', value: fmtInr(a.revenue),
          }))} />
        </div>

        {/* ============ AI INSIGHTS ============ */}
        {insights && (
          <div className="bg-gradient-to-br from-teal-50 via-white to-blue-50 border border-teal-200 rounded-xl p-4" data-testid="one-view-insights">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-teal-600" />
              <h3 className="text-sm font-bold text-slate-900">AI Insights</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
              <InsightPill icon={Award} label="Best Campaign" value={insights.best?.name} sub={`ROAS ${fmtRoas(insights.best?.roas)}`} accent="text-emerald-600" />
              <InsightPill icon={AlertTriangle} label="Worst Campaign" value={insights.worst?.name} sub={`ROAS ${fmtRoas(insights.worst?.roas)}`} accent="text-rose-600" />
              <InsightPill icon={Users} label="Best Brand" value={insights.bestBrand?.name} sub={`${fmtInr(insights.bestBrand?.revenue)} · ${fmtRoas(insights.bestBrand?.roas)}`} accent="text-blue-600" />
              <InsightPill icon={Tag} label="Best Cohort" value={insights.bestCohort?.name || '—'} sub={insights.bestCohort ? `${fmtInr(insights.bestCohort.revenue)} · ${fmtPct(insights.bestCohort.ctr)} CTR` : ''} accent="text-purple-600" />
            </div>
            {insights.bestLp && (
              <div className="mb-3 p-2.5 bg-white/70 border border-slate-200 rounded-lg text-xs" data-testid="best-landing-page">
                <div className="flex items-center gap-2 text-slate-500 mb-0.5">
                  <LinkIcon size={11} className="text-teal-600" />
                  <span className="font-semibold uppercase tracking-wider text-[10px]">Best Landing Page</span>
                </div>
                <a href={insights.bestLp.url} target="_blank" rel="noopener noreferrer"
                  className="text-teal-700 hover:text-teal-900 font-medium hover:underline break-all">
                  {trunc(insights.bestLp.url, 80)}
                </a>
                <span className="text-slate-500 ml-2">— {fmtInr(insights.bestLp.revenue)} across {insights.bestLp.count} runs</span>
              </div>
            )}
            <div className="space-y-1.5">
              {insights.suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-slate-700">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-teal-500 text-white text-[10px] font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                  <p>{s}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ============ CAMPAIGN TABLE ============ */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="one-view-table-wrap">
          <div className="flex items-center justify-between p-3 border-b border-slate-100 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-slate-900">Campaigns ({tableRows.length})</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" value={tableSearch} onChange={(e) => setTableSearch(e.target.value)}
                  placeholder="Search campaign…" className="pl-7 pr-2 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg w-56"
                  data-testid="one-view-table-search" />
              </div>
              <ColumnsPopover
                cols={TABLE_COLS}
                visible={visibleCols}
                onChange={setVisibleCols}
              />
              <button
                onClick={openCompare}
                disabled={selectedRows.length < 2 || selectedRows.length > 5}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: TEAL }}
                data-testid="one-view-compare-btn"
              >
                Compare ({selectedRows.length})
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] font-semibold">
                <tr>
                  <th className="px-2 py-2.5 text-left w-8"><input type="checkbox"
                    checked={tableRows.length > 0 && tableRows.every((r) => selectedRows.includes(r.id))}
                    onChange={(e) => setSelectedRows(e.target.checked ? tableRows.map((r) => r.id) : [])}
                    data-testid="one-view-select-all" /></th>
                  {visibleCols.includes('creative')     && <th className="px-2 py-2.5 text-left">Creative</th>}
                  {visibleCols.includes('name')         && <SortHeader label="Campaign"   k="name"          cur={sortKey} dir={sortDir} onClick={toggleSort} />}
                  {visibleCols.includes('brand_id')     && <SortHeader label="Brand"      k="brand_id"      cur={sortKey} dir={sortDir} onClick={toggleSort} />}
                  {visibleCols.includes('date')         && <SortHeader label="Date"       k="date"          cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('sales')        && <SortHeader label="Revenue"    k="sales"         cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('net_spends')   && <SortHeader label="Spend"      k="net_spends"    cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('roas')         && <SortHeader label="ROAS"       k="roas"          cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('ctr')          && <SortHeader label="CTR"        k="ctr"           cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('delivery_rate') && <SortHeader label="Delivery"  k="delivery_rate" cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('sent')         && <SortHeader label="Sent"       k="sent"          cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('delivered')    && <SortHeader label="Delivered"  k="delivered"     cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('seen')         && <SortHeader label="Seen"       k="seen"          cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('clicks')       && <SortHeader label="Clicks"     k="clicks"        cur={sortKey} dir={sortDir} onClick={toggleSort} align="right" />}
                  {visibleCols.includes('cohort')       && <th className="px-2 py-2.5 text-left">Cohort</th>}
                  {visibleCols.includes('attribution_setting') && <th className="px-2 py-2.5 text-left">Attribution</th>}
                  {visibleCols.includes('landing_page') && <th className="px-2 py-2.5 text-left">Landing</th>}
                </tr>
              </thead>
              <tbody>
                {tableRows.length === 0 ? (
                  <tr><td colSpan={visibleCols.length + 1} className="p-6 text-center text-slate-400">No campaigns match your filters.</td></tr>
                ) : tableRows.map((c) => {
                  const brand = brandMap[c.brand_id];
                  const selected = selectedRows.includes(c.id);
                  return (
                    <tr key={c.id} className={`border-t border-slate-100 hover:bg-slate-50 ${selected ? 'bg-teal-50/60' : ''}`} data-testid={`one-view-row-${c.id}`}>
                      <td className="px-2 py-2"><input type="checkbox" checked={selected} onChange={() => toggleRow(c.id)} data-testid={`row-select-${c.id}`} /></td>
                      {visibleCols.includes('creative') && (
                        <td className="px-2 py-2">
                          {c.image || c.creative ? (
                            <div className="w-10 h-10 rounded overflow-hidden bg-slate-100">
                              <CreativeImage src={c.image || c.creative} alt="" className="w-full h-full object-cover" />
                            </div>
                          ) : <div className="w-10 h-10 rounded bg-slate-100" />}
                        </td>
                      )}
                      {visibleCols.includes('name') && (
                        <td className="px-2 py-2 font-medium text-slate-900 max-w-[220px] truncate" title={c.name}>{c.name || '—'}</td>
                      )}
                      {visibleCols.includes('brand_id') && (
                        <td className="px-2 py-2">
                          {brand && (
                            <span className="inline-flex items-center gap-1.5 text-slate-700">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: brand.color }} />
                              {brand.name}
                            </span>
                          )}
                        </td>
                      )}
                      {visibleCols.includes('date') && <td className="px-2 py-2 text-right text-slate-600">{c.date ? fmtFullDate(c.date) : '—'}</td>}
                      {visibleCols.includes('sales') && <td className="px-2 py-2 text-right font-semibold text-teal-700">{fmtInr(c.sales)}</td>}
                      {visibleCols.includes('net_spends') && <td className="px-2 py-2 text-right text-slate-700">{fmtInr(c.net_spends)}</td>}
                      {visibleCols.includes('roas') && <td className="px-2 py-2 text-right font-semibold text-orange-600">{fmtRoas(c.roas)}</td>}
                      {visibleCols.includes('ctr') && <td className="px-2 py-2 text-right text-slate-700">{c.ctr != null && c.ctr !== '' ? fmtPct(c.ctr) : '—'}</td>}
                      {visibleCols.includes('delivery_rate') && <td className="px-2 py-2 text-right text-slate-700">{c.delivery_rate != null && c.delivery_rate !== '' ? fmtPct(c.delivery_rate) : '—'}</td>}
                      {visibleCols.includes('sent') && <td className="px-2 py-2 text-right text-slate-700">{fmtNum(c.sent)}</td>}
                      {visibleCols.includes('delivered') && <td className="px-2 py-2 text-right text-slate-700">{fmtNum(c.delivered)}</td>}
                      {visibleCols.includes('seen') && <td className="px-2 py-2 text-right text-slate-700">{fmtNum(c.seen)}</td>}
                      {visibleCols.includes('clicks') && <td className="px-2 py-2 text-right text-slate-700">{fmtNum(c.clicks)}</td>}
                      {visibleCols.includes('cohort') && <td className="px-2 py-2 text-slate-700 max-w-[140px] truncate" title={c.cohort}>{c.cohort || '—'}</td>}
                      {visibleCols.includes('attribution_setting') && <td className="px-2 py-2 text-slate-700 max-w-[140px] truncate" title={c.attribution_setting}>{c.attribution_setting || '—'}</td>}
                      {visibleCols.includes('landing_page') && (
                        <td className="px-2 py-2">
                          {c.landing_page ? (
                            <a href={c.landing_page} target="_blank" rel="noopener noreferrer" title={c.landing_page}
                              className="inline-flex items-center gap-1 text-teal-700 hover:text-teal-900 hover:underline max-w-[180px] truncate">
                              {trunc(c.landing_page, 28)}
                              <ExternalLink size={9} />
                            </a>
                          ) : '—'}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ============ COMPARE OVERLAY ============ */}
      {compareOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm p-4 md:p-8 overflow-y-auto" data-testid="one-view-compare-overlay">
          <div className="max-w-7xl mx-auto bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <div className="flex items-center gap-2 text-white">
                <BarChart3 size={18} className="text-teal-400" />
                <h2 className="text-base font-bold">Comparing {compareItems.length} campaigns</h2>
              </div>
              <button onClick={() => setCompareOpen(false)}
                className="px-3 py-1.5 text-xs rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
                data-testid="one-view-compare-close">
                Close
              </button>
            </div>
            <div className="p-4 grid gap-4"
              style={{ gridTemplateColumns: `repeat(${Math.min(compareItems.length, 5)}, minmax(240px, 1fr))` }}>
              {compareItems.map((c) => {
                const brand = brandMap[c.brand_id];
                const tag = pickTag(c.roas);
                const best = (k) => Number(c[k] || 0) === compareBest[k] && compareBest[k] > 0;
                return (
                  <div key={c.id} className="bg-slate-800/60 border border-slate-700 rounded-xl overflow-hidden" data-testid={`one-view-compare-card-${c.id}`}>
                    <div className="relative aspect-square bg-slate-900">
                      <CreativeImage src={c.image || c.creative} alt={c.name} className="w-full h-full object-cover" />
                      {c.roas != null && (
                        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full bg-gradient-to-r ${tag.accent} text-white text-[11px] font-bold`}>
                          <Target size={10} className="inline mr-1" />{fmtRoas(c.roas)}
                        </div>
                      )}
                      <div className={`absolute bottom-2 left-2 px-2 py-1 rounded-full bg-gradient-to-r ${tag.accent} text-white text-[10px] font-semibold`}>
                        {tag.label}
                      </div>
                    </div>
                    <div className="p-3 space-y-2">
                      <h4 className="text-sm font-bold text-white truncate" title={c.name}>{c.name || 'Untitled'}</h4>
                      {brand && (
                        <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: brand.color }} />
                          {brand.name} · {c.date}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-1.5 text-[11px]">
                        <CompareMini label="Revenue" value={fmtInr(c.sales)} best={best('sales')} />
                        <CompareMini label="Spend" value={fmtInr(c.net_spends)} best={best('net_spends')} />
                        <CompareMini label="CTR" value={fmtPct(c.ctr)} best={best('ctr')} />
                        <CompareMini label="Delivery" value={fmtPct(c.delivery_rate)} best={best('delivery_rate')} />
                      </div>
                      <div className="flex flex-col gap-1 text-[10px]">
                        {c.cohort && <div className="flex items-center gap-1.5 text-slate-400"><Users size={10} className="text-cyan-400" /><span>{c.cohort}</span></div>}
                        {c.attribution_setting && <div className="flex items-center gap-1.5 text-slate-400"><Tag size={10} className="text-pink-400" /><span>{c.attribution_setting}</span></div>}
                        {c.landing_page && (
                          <a href={c.landing_page} target="_blank" rel="noopener noreferrer" title={c.landing_page}
                            className="flex items-center gap-1.5 text-teal-300 hover:text-teal-200 truncate">
                            <LinkIcon size={10} /><span className="truncate">{trunc(c.landing_page, 24)}</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Sub-components ====================
const SortHeader = ({ label, k, cur, dir, onClick, align = 'left' }) => (
  <th
    onClick={() => onClick(k)}
    className={`px-2 py-2.5 cursor-pointer select-none hover:text-slate-800 text-${align}`}
    data-testid={`sort-${k}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {cur === k && (dir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />)}
    </span>
  </th>
);

const ChartCard = ({ title, testid, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
    <h4 className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
      <BarChart3 size={12} className="text-teal-600" /> {title}
    </h4>
    {children}
  </div>
);

const LeaderCard = ({ title, icon: Icon, accent, items }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`leader-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`flex items-center gap-1.5 mb-2 ${accent}`}>
      <Icon size={12} />
      <h4 className="text-[11px] font-bold uppercase tracking-wider">{title}</h4>
    </div>
    <div className="space-y-1.5">
      {items.length === 0 ? (
        <p className="text-[11px] text-slate-400">No data</p>
      ) : items.map((it, i) => (
        <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <span className="text-slate-400 font-mono text-[10px]">{i + 1}.</span>
            {it.color && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: it.color }} />}
            <div className="min-w-0 flex-1">
              <p className="text-slate-800 font-medium truncate" title={it.label}>{trunc(it.label, 22)}</p>
              {it.sub && <p className="text-slate-400 truncate text-[10px]">{it.sub}</p>}
            </div>
          </div>
          <span className="font-semibold text-slate-900 flex-shrink-0">{it.value}</span>
        </div>
      ))}
    </div>
  </div>
);

const InsightPill = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white/80 border border-slate-200 rounded-lg p-2.5" data-testid={`insight-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`flex items-center gap-1.5 mb-1 ${accent}`}>
      <Icon size={12} />
      <span className="text-[10px] font-bold uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-xs font-semibold text-slate-900 truncate" title={value}>{value || '—'}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

const CompareMini = ({ label, value, best }) => (
  <div className={`rounded p-1.5 border ${best ? 'border-emerald-500/60 bg-emerald-500/10' : 'border-slate-700 bg-slate-900/60'}`}>
    <p className="text-[9px] text-slate-400 uppercase tracking-wide font-medium">{label}</p>
    <p className="text-xs font-bold text-white flex items-center justify-between">
      <span>{value}</span>
      {best && <span className="text-[8px] font-bold text-emerald-400">BEST</span>}
    </p>
  </div>
);

// ==================== Saved Views Picker ====================
// Everything about saved-view UX lives here so the parent stays focused on
// data + effects. `readOnly` hides create/update/rename/delete/set-default.
const SavedViewsPicker = ({ views, currentId, onApply, onCreate, onOverwrite, onRename, onDelete, onSetDefault, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) { setOpen(false); setCreating(false); setRenamingId(null); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const current = views.find((v) => v.id === currentId);
  const defaultView = views.find((v) => v.is_default);
  const buttonLabel = current?.name || (defaultView ? defaultView.name : 'Saved Views');

  const submitCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    const res = await onCreate?.(name);
    if (res) { setCreating(false); setNewName(''); }
  };

  const submitRename = async (id) => {
    const name = renameValue.trim();
    if (!name) return;
    await onRename?.(id, name);
    setRenamingId(null); setRenameValue('');
  };

  return (
    <div ref={boxRef} className="relative" data-testid="saved-views-picker">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-teal-400 text-xs font-medium text-slate-700"
        data-testid="saved-views-toggle"
      >
        <Bookmark size={12} className="text-teal-600" />
        <span className="max-w-[160px] truncate">{buttonLabel}</span>
        {current?.is_default && <Star size={10} className="text-amber-500 fill-amber-500" />}
        <ChevronDown size={11} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="saved-views-menu">
          {/* Header */}
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Saved Views</span>
            {!readOnly && (
              <button
                onClick={() => { setCreating(true); setNewName(''); }}
                className="text-[11px] text-teal-600 font-semibold hover:underline inline-flex items-center gap-1"
                data-testid="saved-views-new-btn"
              >
                <BookmarkPlus size={11} /> New
              </button>
            )}
          </div>

          {/* Create form */}
          {creating && !readOnly && (
            <div className="p-3 border-b border-slate-100 bg-teal-50/40" data-testid="saved-views-create-form">
              <label className="block text-[10px] text-slate-500 font-semibold uppercase tracking-wider mb-1">Name your view</label>
              <input
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
                placeholder="e.g. Weekly YouWeFit Review"
                maxLength={80}
                className="w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded"
                data-testid="saved-views-create-input"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={submitCreate}
                  disabled={!newName.trim()}
                  className="flex-1 px-2 py-1.5 text-xs font-semibold rounded text-white disabled:opacity-40"
                  style={{ backgroundColor: TEAL }}
                  data-testid="saved-views-create-submit"
                >
                  <Save size={11} className="inline mr-1" /> Save current
                </button>
                <button
                  onClick={() => { setCreating(false); setNewName(''); }}
                  className="px-2 py-1.5 text-xs rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Update current view */}
          {!readOnly && current && !creating && (
            <div className="p-2 border-b border-slate-100 bg-amber-50/60 flex items-center justify-between" data-testid="saved-views-current-actions">
              <span className="text-[11px] text-amber-800">
                Editing <b>{current.name}</b>
              </span>
              <button
                onClick={onOverwrite}
                className="text-[11px] text-amber-700 font-semibold hover:underline inline-flex items-center gap-1"
                data-testid="saved-views-update-current"
              >
                <Save size={10} /> Update
              </button>
            </div>
          )}

          {/* List */}
          <div className="max-h-72 overflow-y-auto">
            {views.length === 0 ? (
              <div className="p-4 text-xs text-slate-400 text-center">No saved views yet.</div>
            ) : views.map((v) => (
              <div
                key={v.id}
                className={`px-3 py-2 border-b border-slate-100 last:border-b-0 group ${currentId === v.id ? 'bg-teal-50' : 'hover:bg-slate-50'}`}
                data-testid={`saved-view-row-${v.id}`}
              >
                {renamingId === v.id ? (
                  <div className="flex items-center gap-1">
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') submitRename(v.id); if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); } }}
                      maxLength={80}
                      className="flex-1 px-2 py-1 text-xs bg-white border border-slate-200 rounded"
                      data-testid={`saved-view-rename-input-${v.id}`}
                    />
                    <button onClick={() => submitRename(v.id)} className="text-teal-600 hover:text-teal-800 p-1" data-testid={`saved-view-rename-save-${v.id}`}>
                      <Check size={12} />
                    </button>
                    <button onClick={() => { setRenamingId(null); setRenameValue(''); }} className="text-slate-400 p-1"><X size={12} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { onApply(v); setOpen(false); }}
                      className="flex-1 text-left min-w-0"
                      data-testid={`saved-view-apply-${v.id}`}
                    >
                      <div className="flex items-center gap-1.5">
                        {v.is_default && <Star size={10} className="text-amber-500 fill-amber-500 flex-shrink-0" />}
                        <span className={`text-xs font-medium truncate ${currentId === v.id ? 'text-teal-700' : 'text-slate-900'}`}>{v.name}</span>
                      </div>
                    </button>
                    {!readOnly && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => onSetDefault?.(v.id)}
                          disabled={v.is_default}
                          title={v.is_default ? 'Default' : 'Set as default'}
                          className="p-1 text-amber-500 hover:text-amber-700 disabled:opacity-30"
                          data-testid={`saved-view-set-default-${v.id}`}
                        >
                          <Star size={11} className={v.is_default ? 'fill-amber-500' : ''} />
                        </button>
                        <button
                          onClick={() => { setRenamingId(v.id); setRenameValue(v.name); }}
                          title="Rename"
                          className="p-1 text-slate-500 hover:text-slate-800"
                          data-testid={`saved-view-rename-${v.id}`}
                        >
                          <Edit3 size={11} />
                        </button>
                        <button
                          onClick={() => { if (window.confirm(`Delete view "${v.name}"?`)) onDelete?.(v.id); }}
                          title="Delete"
                          className="p-1 text-rose-500 hover:text-rose-700"
                          data-testid={`saved-view-delete-${v.id}`}
                        >
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== Columns visibility popover ====================
const ColumnsPopover = ({ cols, visible, onChange }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div ref={boxRef} className="relative" data-testid="columns-popover">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-teal-400 text-xs font-medium text-slate-700"
        data-testid="columns-toggle"
      >
        <LayoutGrid size={11} /> Columns ({visible.length}/{cols.length})
        <ChevronDown size={11} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden" data-testid="columns-menu">
          <div className="p-2 border-b border-slate-100 flex items-center justify-between text-[11px]">
            <button onClick={() => onChange(cols.map((c) => c.key))} className="text-teal-600 font-semibold hover:underline">Show all</button>
            <button onClick={() => onChange(cols.filter((c) => c.always).map((c) => c.key))} className="text-slate-500 font-semibold hover:underline">Reset</button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {cols.map((c) => {
              const checked = visible.includes(c.key);
              return (
                <button
                  key={c.key}
                  type="button"
                  disabled={c.always && checked}
                  onClick={() => {
                    if (c.always && checked) return;
                    onChange(checked ? visible.filter((k) => k !== c.key) : [...visible, c.key]);
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 text-left ${c.always && checked ? 'opacity-70 cursor-not-allowed' : ''}`}
                  data-testid={`column-toggle-${c.key}`}
                >
                  <span className={`w-3.5 h-3.5 rounded flex items-center justify-center border ${checked ? 'bg-teal-500 border-teal-500' : 'border-slate-300 bg-white'}`}>
                    {checked && <Check size={9} className="text-white" />}
                  </span>
                  <span className="text-slate-700 flex-1">{c.label}</span>
                  {c.always && <span className="text-[9px] text-slate-400">required</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default OneViewAnalyzer;
